import { createContext, useContext } from 'react';

import type { TenantMember } from '~/features/organization/types/member.types';
import type { ActorRef } from '~/types/actor.types';

/**
 * STOPGAP — the pure half of the client-side actor fallback.
 *
 * The API is meant to name actors itself: its `ActorDirectory` resolves actor
 * UUIDs through identity-service and ships `displayName` filled in. When it
 * comes back `resolved: false` the label degrades to a short id
 * (`User 019e…4c2e`) — which happens for every row written before the actor
 * migration, since it deliberately ran no backfill, and whenever the directory
 * warm is misconfigured or failing.
 *
 * This is **not** a replacement for fixing server-side resolution, and it
 * cannot resolve more cases than the backend can: it reads the same
 * per-subsidiary membership list, so a departed user or a service account stays
 * unresolved here too. Delete this file, its provider, and their imports once
 * the API returns names reliably.
 *
 * Fetching lives in `ActorResolverProvider`, not here: `ActorLabel` is
 * presentational and is rendered by tests that supply neither a
 * QueryClientProvider nor a router, so the consumer hook must stay free of
 * both and fall back to leaving the server's label untouched.
 */

/** Resolves an actor, or returns it unchanged. */
export type ResolveActor = (actor: ActorRef | null | undefined) => ActorRef | null;

/** Default: hand back exactly what the server sent. */
const identityResolver: ResolveActor = (actor) => actor ?? null;

export const ActorResolverContext = createContext(identityResolver);

/** Kinds that are already their own correct answer — never overwrite them with
 *  a member lookup. A service principal's id is not a member id, and
 *  SCHEDULE/SYSTEM are sentinels the backend resolved on purpose. */
const NON_HUMAN_KINDS = new Set(['SCHEDULE', 'SERVICE', 'SYSTEM']);

/** First + last name, falling back to whichever half exists. Mirrors the
 *  backend's `DirectoryUser.display_name()` — both halves are nullable and
 *  there is no full-name column, so assembling the label is the client's job. */
function memberDisplayName(member: TenantMember): string | null {
  const parts = [member.firstName, member.lastName]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Builds a resolver over an id → member map. Pure, so the upgrade rules are
 * separable from how the members were fetched.
 */
export function buildActorResolver(byId: Map<string, TenantMember>): ResolveActor {
  return (actor) => {
    if (!actor) return null;
    // Already named by the server, a sentinel, or has no id to look up.
    if (actor.resolved || !actor.id || NON_HUMAN_KINDS.has(actor.kind)) return actor;

    const member = byId.get(actor.id);
    if (!member) return actor;

    const displayName = memberDisplayName(member);
    // A member row with neither name half is no better than the short id — an
    // empty label would be worse than a terse one.
    if (!displayName) return actor;

    return {
      ...actor,
      displayName,
      kind: 'USER',
      email: member.email,
      avatarUrl: member.avatarUrl ?? null,
      resolved: true,
    };
  };
}

/**
 * Returns the actor resolver. With no provider mounted this is the identity
 * function, so a consumer renders the server's own label rather than throwing.
 */
export function useActorResolver(): ResolveActor {
  return useContext(ActorResolverContext);
}
